import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Gift, Coins, CheckCircle2, Clock, XCircle, Loader2, History, X, ChevronLeft, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
    const styles = {
        pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Ausstehend' },
        approved: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Genehmigt' },
        rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Abgelehnt' }
    };
    const style = styles[status] || styles.pending;
    
    return (
        <Badge variant="outline" className={`${style.bg} ${style.text} ${style.border}`}>
            {style.label}
        </Badge>
    );
};

const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${process.env.REACT_APP_BACKEND_URL}${image}`;
};

// Reusable Reward Card Component
const RewardCard = ({ reward, user, tenant, redemptions, onPreview }) => {
    const isEligible = (user?.points_balance || 0) >= reward.points_required;
    const hasPending = redemptions.some(
        r => r.reward_id === reward.id && r.status === 'pending'
    );
    
    return (
        <Card 
            className={`transition-all cursor-pointer hover:shadow-lg ${isEligible && !hasPending ? 'ring-2 ring-green-200' : ''}`}
            onClick={() => onPreview(reward)}
            data-testid={`reward-card-${reward.id}`}
        >
            <CardContent className="p-4">
                {/* Image Preview */}
                <div className="relative mb-4">
                    {reward.image ? (
                        <div className="w-full h-36 rounded-xl overflow-hidden bg-slate-100">
                            <img 
                                src={getImageUrl(reward.image)} 
                                alt={reward.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    ) : (
                        <div 
                            className="w-full h-36 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${tenant?.primary_color}10` }}
                        >
                            <Gift size={48} style={{ color: tenant?.primary_color }} className="opacity-50" />
                        </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className="absolute top-2 right-2">
                        {hasPending ? (
                            <Badge className="bg-amber-100 text-amber-700 border-0 shadow-sm">
                                <Clock size={12} className="mr-1" />
                                Ausstehend
                            </Badge>
                        ) : isEligible ? (
                            <Badge className="bg-green-100 text-green-700 border-0 shadow-sm">
                                <CheckCircle2 size={12} className="mr-1" />
                                Einlösbar
                            </Badge>
                        ) : null}
                    </div>
                </div>
                
                <h3 className="text-lg font-semibold text-slate-900 mb-1 line-clamp-1">
                    {reward.title}
                </h3>
                {reward.description && (
                    <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                        {reward.description}
                    </p>
                )}
                
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span 
                        className="text-lg font-bold"
                        style={{ color: tenant?.primary_color }}
                    >
                        {reward.points_required.toLocaleString('de-DE')} Pkt.
                    </span>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-500"
                        onClick={(e) => {
                            e.stopPropagation();
                            onPreview(reward);
                        }}
                    >
                        Details →
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export const RewardsPage = () => {
    const { token, user, refreshUser } = useAuth();
    const { tenant } = useTenant();
    
    const [sections, setSections] = useState([]);
    const [rewards, setRewards] = useState([]);
    const [redemptions, setRedemptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedReward, setSelectedReward] = useState(null);
    const [previewReward, setPreviewReward] = useState(null);
    const [redeeming, setRedeeming] = useState(false);
    const [activeTab, setActiveTab] = useState('rewards');
    const [selectedSection, setSelectedSection] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [sectionsRes, rewardsRes, redemptionsRes] = await Promise.all([
                axios.get(`${API}/reward-sections`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/rewards`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/redemptions/my`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setSections(sectionsRes.data);
            setRewards(rewardsRes.data);
            setRedemptions(redemptionsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Get rewards without a section (displayed directly)
    const uncategorizedRewards = rewards.filter(r => !r.section_id);
    
    // Get rewards for selected section
    const sectionRewards = selectedSection 
        ? rewards.filter(r => r.section_id === selectedSection.id)
        : [];

    const handleRedeem = async () => {
        if (!selectedReward) return;
        
        setRedeeming(true);
        try {
            await axios.post(
                `${API}/redemptions`,
                { reward_id: selectedReward.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Einlösung erfolgreich angefordert!');
            setSelectedReward(null);
            setPreviewReward(null);
            fetchData();
            if (refreshUser) refreshUser();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Einlösung fehlgeschlagen');
        } finally {
            setRedeeming(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const openPreview = (reward) => {
        setPreviewReward(reward);
    };

    const startRedeem = (reward) => {
        setPreviewReward(null);
        setSelectedReward(reward);
    };

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900">Prämienkatalog</h1>
                        <p className="text-slate-600 mt-1">
                            Lösen Sie Ihre Punkte gegen attraktive Prämien ein
                        </p>
                    </div>
                    <Card className="sm:w-auto" data-testid="points-balance-card">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${tenant?.primary_color}15` }}
                            >
                                <Coins size={20} style={{ color: tenant?.primary_color }} />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Ihre Punkte</p>
                                <p className="text-xl font-bold text-slate-900">
                                    {user?.points_balance?.toLocaleString('de-DE') || 0}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="rewards" className="gap-2">
                            <Gift size={16} />
                            Prämien
                        </TabsTrigger>
                        <TabsTrigger value="history" className="gap-2">
                            <History size={16} />
                            Meine Einlösungen
                            {redemptions.filter(r => r.status === 'pending').length > 0 && (
                                <Badge className="ml-1 bg-amber-100 text-amber-700 text-xs px-1.5">
                                    {redemptions.filter(r => r.status === 'pending').length}
                                </Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="rewards" className="mt-6">
                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {[1, 2, 3, 4, 5, 6].map(i => (
                                    <Card key={i} className="animate-pulse">
                                        <CardContent className="p-6">
                                            <div className="w-full h-32 bg-slate-200 rounded-xl mb-4" />
                                            <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                                            <div className="h-4 bg-slate-200 rounded w-1/2" />
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : selectedSection ? (
                            /* Section Detail View */
                            <div className="space-y-6">
                                <div className="flex items-center gap-4">
                                    <Button 
                                        variant="ghost" 
                                        onClick={() => setSelectedSection(null)}
                                        className="gap-2"
                                    >
                                        <ChevronLeft size={18} />
                                        Zurück zur Übersicht
                                    </Button>
                                    <h2 className="text-xl font-semibold text-slate-900">
                                        {selectedSection.name}
                                    </h2>
                                </div>
                                
                                {sectionRewards.length === 0 ? (
                                    <Card>
                                        <CardContent className="p-12 text-center">
                                            <Gift size={64} className="mx-auto text-slate-300 mb-4" />
                                            <h3 className="text-xl font-semibold text-slate-900 mb-2">
                                                Keine Prämien in dieser Kategorie
                                            </h3>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {sectionRewards.map((reward) => (
                                            <RewardCard 
                                                key={reward.id}
                                                reward={reward}
                                                user={user}
                                                tenant={tenant}
                                                redemptions={redemptions}
                                                onPreview={openPreview}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Main View with Sections and Uncategorized Rewards */
                            <div className="space-y-8">
                                {/* Section Cards */}
                                {sections.length > 0 && (
                                    <div>
                                        <h2 className="text-lg font-semibold text-slate-900 mb-4">Kategorien</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {sections.map((section) => (
                                                <Card 
                                                    key={section.id}
                                                    className="cursor-pointer transition-all hover:shadow-lg hover:ring-2 ring-slate-200"
                                                    onClick={() => setSelectedSection(section)}
                                                    data-testid={`section-card-${section.id}`}
                                                >
                                                    <CardContent className="p-0">
                                                        {section.image ? (
                                                            <div className="w-full h-36 rounded-t-lg overflow-hidden">
                                                                <img 
                                                                    src={getImageUrl(section.image)} 
                                                                    alt={section.name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div 
                                                                className="w-full h-36 rounded-t-lg flex items-center justify-center"
                                                                style={{ backgroundColor: `${tenant?.primary_color}10` }}
                                                            >
                                                                <FolderOpen size={48} style={{ color: tenant?.primary_color }} className="opacity-50" />
                                                            </div>
                                                        )}
                                                        <div className="p-4">
                                                            <h3 className="text-lg font-semibold text-slate-900 mb-1">
                                                                {section.name}
                                                            </h3>
                                                            {section.description && (
                                                                <p className="text-sm text-slate-500 mb-2 line-clamp-2">
                                                                    {section.description}
                                                                </p>
                                                            )}
                                                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                                                <Gift size={16} style={{ color: tenant?.primary_color }} />
                                                                {section.reward_count} Prämie{section.reward_count !== 1 ? 'n' : ''}
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Uncategorized Rewards */}
                                {uncategorizedRewards.length > 0 && (
                                    <div>
                                        {sections.length > 0 && (
                                            <h2 className="text-lg font-semibold text-slate-900 mb-4">
                                                Weitere Prämien
                                            </h2>
                                        )}
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="rewards-grid">
                                            {uncategorizedRewards.map((reward) => (
                                                <RewardCard 
                                                    key={reward.id}
                                                    reward={reward}
                                                    user={user}
                                                    tenant={tenant}
                                                    redemptions={redemptions}
                                                    onPreview={openPreview}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Empty State */}
                                {rewards.length === 0 && sections.length === 0 && (
                                    <Card>
                                        <CardContent className="p-12 text-center">
                                            <Gift size={64} className="mx-auto text-slate-300 mb-4" />
                                            <h3 className="text-xl font-semibold text-slate-900 mb-2">
                                                Keine Prämien verfügbar
                                            </h3>
                                            <p className="text-slate-500">
                                                Schauen Sie später wieder vorbei!
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="history" className="mt-6">
                        {redemptions.length === 0 ? (
                            <Card>
                                <CardContent className="p-12 text-center">
                                    <History size={64} className="mx-auto text-slate-300 mb-4" />
                                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                                        Keine Einlösungen
                                    </h3>
                                    <p className="text-slate-500">
                                        Sie haben noch keine Prämien eingelöst.
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-4">
                                {redemptions.map((redemption) => (
                                    <Card key={redemption.id} data-testid={`redemption-${redemption.id}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div 
                                                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                                                        style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                                    >
                                                        <Gift size={24} style={{ color: tenant?.primary_color }} />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-slate-900">
                                                            {redemption.reward_title}
                                                        </h4>
                                                        <p className="text-sm text-slate-500">
                                                            {formatDate(redemption.created_at)} • {redemption.points_used.toLocaleString('de-DE')} Punkte
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <StatusBadge status={redemption.status} />
                                                    {redemption.admin_notes && (
                                                        <p className="text-xs text-slate-500 mt-1 max-w-xs">
                                                            {redemption.admin_notes}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>

            {/* Preview Dialog */}
            <Dialog open={!!previewReward} onOpenChange={() => setPreviewReward(null)}>
                <DialogContent className="sm:max-w-lg" aria-describedby="reward-preview-description">
                    <DialogHeader>
                        <DialogTitle className="text-xl">{previewReward?.title}</DialogTitle>
                        <DialogDescription id="reward-preview-description" className="sr-only">
                            Vorschau der Prämie mit Bild und Details
                        </DialogDescription>
                    </DialogHeader>
                    
                    {previewReward && (
                        <div className="space-y-4">
                            {/* Large Image */}
                            {previewReward.image ? (
                                <div className="w-full h-56 rounded-xl overflow-hidden bg-slate-100">
                                    <img 
                                        src={getImageUrl(previewReward.image)} 
                                        alt={previewReward.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            ) : (
                                <div 
                                    className="w-full h-56 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: `${tenant?.primary_color}10` }}
                                >
                                    <Gift size={64} style={{ color: tenant?.primary_color }} className="opacity-50" />
                                </div>
                            )}
                            
                            {/* Description */}
                            {previewReward.description && (
                                <p className="text-slate-600">{previewReward.description}</p>
                            )}
                            
                            {/* Points */}
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                                <span className="text-slate-600">Benötigte Punkte</span>
                                <span 
                                    className="text-2xl font-bold"
                                    style={{ color: tenant?.primary_color }}
                                >
                                    {previewReward.points_required.toLocaleString('de-DE')} Pkt.
                                </span>
                            </div>
                            
                            {/* User Points */}
                            <div className="flex items-center justify-between p-4 border rounded-xl">
                                <span className="text-slate-600">Ihre Punkte</span>
                                <span className="text-xl font-semibold text-slate-900">
                                    {(user?.points_balance || 0).toLocaleString('de-DE')} Pkt.
                                </span>
                            </div>
                            
                            {/* Status message */}
                            {redemptions.some(r => r.reward_id === previewReward.id && r.status === 'pending') ? (
                                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                    <p className="text-sm text-amber-800 flex items-center gap-2">
                                        <Clock size={16} />
                                        Sie haben bereits eine ausstehende Anfrage für diese Prämie.
                                    </p>
                                </div>
                            ) : (user?.points_balance || 0) < previewReward.points_required ? (
                                <div className="p-3 bg-slate-50 rounded-lg border">
                                    <p className="text-sm text-slate-600">
                                        Sie benötigen noch <strong>{(previewReward.points_required - (user?.points_balance || 0)).toLocaleString('de-DE')}</strong> Punkte, um diese Prämie einzulösen.
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    )}
                    
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setPreviewReward(null)}>
                            Schließen
                        </Button>
                        {previewReward && (
                            <Button
                                onClick={() => startRedeem(previewReward)}
                                disabled={
                                    (user?.points_balance || 0) < previewReward.points_required ||
                                    redemptions.some(r => r.reward_id === previewReward.id && r.status === 'pending')
                                }
                                style={{ backgroundColor: tenant?.primary_color }}
                            >
                                Jetzt einlösen
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={!!selectedReward} onOpenChange={() => setSelectedReward(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Prämie einlösen</DialogTitle>
                        <DialogDescription>
                            Möchten Sie diese Prämie wirklich einlösen?
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedReward && (
                        <div className="py-4">
                            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                                {selectedReward.image ? (
                                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200">
                                        <img 
                                            src={getImageUrl(selectedReward.image)} 
                                            alt={selectedReward.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ) : (
                                    <div 
                                        className="w-16 h-16 rounded-xl flex items-center justify-center"
                                        style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                    >
                                        <Gift size={28} style={{ color: tenant?.primary_color }} />
                                    </div>
                                )}
                                <div>
                                    <h4 className="font-semibold text-slate-900">{selectedReward.title}</h4>
                                    <p className="text-sm text-slate-500">
                                        {selectedReward.points_required.toLocaleString('de-DE')} Punkte werden abgezogen
                                    </p>
                                </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <p className="text-sm text-amber-800">
                                    Nach der Einlösung wird Ihre Anfrage von einem Administrator geprüft. 
                                    Die Punkte werden sofort abgezogen.
                                </p>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedReward(null)}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleRedeem}
                            disabled={redeeming}
                            style={{ backgroundColor: tenant?.primary_color }}
                        >
                            {redeeming ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Wird eingelöst...
                                </>
                            ) : (
                                'Jetzt einlösen'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
};
